import React, { useMemo, useState } from 'react';

import { API } from 'constants';
import client from '../../../../../services/library-api';

import { useFormState } from '../../../../../hooks/useFormState';

import {
    defaultErrorMessage,
    emptyFieldErrorMessage,
} from '../../../../../constants/errorMessages';

import Panel from '../../../../../components/Panel';
import ErrorAlert from '../../../../../design-system/ErrorAlert/ErrorAlert';
import PanelLayout from '../../../../../components/PanelLayout/PanelLayout';
import PlaybookViewPanelForm from '../PlaybookViewPanelForm/PlaybookViewPanelForm';
import { Button } from '../../../../../design-system';

const PlaybookViewSidePanel = ({
    mode = 'create', // "create" | "edit"
    data,
    playbookId,
    setViews,
    onClose,
}) => {
    const { formData, setFormData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } =
        useFormState(() => {
            if (mode === 'edit' && data) {
                const { name, description, view_type, view_content } = data;
                return { name, description, view_type, view_content };
            }
            return { name: '', description: '', view_type: null, view_content: null };
        });

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const allRequiredFieldsFilled = useMemo(() => {
        const tabs = formData.view_content?.tabs || [];

        const isLiveTab = !!tabs?.length && tabs.some((tab) => tab.is_live);
        if (!isLiveTab) return false;

        return Object.values(formData).every(Boolean);
    }, [formData]);

    const createView = async (requestBody) => {
        const { data } = await client.post(API.ROUTES.library.playbookView, requestBody);

        setViews((views) => {
            const newViews = [...(views || []), data];
            return newViews.sort((a, b) => a.name.localeCompare(b.name));
        });
    };

    const editView = async (requestBody) => {
        const viewId = data.id;

        const { data: response } = await client.patch(
            API.ROUTES.library.playbookView + viewId + '/',
            requestBody
        );

        setViews((views) => views.map((view) => (view.id === response.id ? response : view)));
    };

    const handleSubmit = async () => {
        try {
            const { name, description, view_type, view_content } = formData;

            if (!allRequiredFieldsFilled) {
                const errorMessages = {
                    name: !name ? emptyFieldErrorMessage : null,
                    description: !description ? emptyFieldErrorMessage : null,
                    view_type: !view_type ? 'Please select a View Type.' : null,
                };
                const tabs = view_content?.tabs || [];
                const isLiveTab = !!tabs?.length && tabs.some((tab) => tab.is_live);
                if (!isLiveTab) {
                    errorMessages.view_content = 'Please add at lest one live Tab.';
                }

                setFieldErrorMessages(errorMessages);

                return;
            }

            setIsLoading(true);

            const requestBody = {
                name,
                description,
                playbook: playbookId,
                view_type,
                view_content,
            };

            const action = {
                create: createView,
                edit: editView,
            }[mode];

            await action(requestBody);

            onClose();
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, status: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const title = {
        create: 'Add New Playbook View',
        edit: 'Edit Playbook View',
    }[mode];

    const buttonText = {
        create: 'Create View',
        edit: 'Save Changes',
    }[mode];

    const submitButtonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled
        ? 'default'
        : 'disabled';

    const renderButtonsGroup = () => {
        return (
            <div className="flex justify-end">
                <Button
                    type="primary"
                    size="sm"
                    text={buttonText}
                    state={submitButtonState}
                    onClick={handleSubmit}
                />
            </div>
        );
    };

    const setTabs = (cb) => {
        setFormData((data) => ({
            ...data,
            view_content: { tabs: cb(data.view_content?.tabs || []) },
        }));
        setFieldErrorMessages((messages) => ({ ...messages, view_content: null }));
    };

    return (
        <Panel onClose={onClose} showControls={false} withOverflowProperty={false} resetPadding>
            <PanelLayout title={title} renderButtonsGroup={renderButtonsGroup} onClose={onClose}>
                <PlaybookViewPanelForm
                    formData={formData}
                    setTabs={setTabs}
                    fieldErrorMessages={fieldErrorMessages}
                    handleInputChange={handleInputChange}
                    playbookId={playbookId}
                />
            </PanelLayout>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Panel>
    );
};

export default PlaybookViewSidePanel;
